import React from 'react'
import { useTranslation } from 'react-i18next'
import { AiChatMarkdown } from '@/features/ai-chat/ai-chat-markdown'
import { cn } from '@/shared/lib/utils'
import { Button } from '@/shared/ui/button'
import { Textarea } from '@/shared/ui/textarea'

export const collectionDescriptionMaxLength = 10000

// Single newlines inside paragraphs stay visible so legacy plain-text
// descriptions look the same as before.
const markdownClassName =
  '[&_li]:whitespace-pre-line [&_p]:whitespace-pre-line text-foreground'

export function CollectionDescriptionMarkdown({
  className,
  value,
}: {
  className?: string
  value: string
}) {
  return (
    <div className={cn(markdownClassName, className)}>
      <AiChatMarkdown content={value} sources={[]} />
    </div>
  )
}

export function CollectionDescriptionMarkdownEditor({
  autoFocus,
  disabled,
  id,
  label,
  onBlur,
  onChange,
  placeholder,
  value,
}: {
  autoFocus?: boolean
  disabled?: boolean
  id?: string
  label?: string
  onBlur?: () => void
  onChange: (value: string) => void
  placeholder?: string
  value: string
}) {
  const { t } = useTranslation('documentCollections')
  const [previewing, setPreviewing] = React.useState(false)

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <PreviewToggle previewing={previewing} onChange={setPreviewing} />
        <span className="text-xs text-muted-foreground">
          {value.length}/{collectionDescriptionMaxLength}
        </span>
      </div>
      {previewing ? (
        <div
          data-testid="collection-description-preview"
          className="min-h-20 rounded-md border border-input px-3 py-2 text-sm"
        >
          {value.trim() ? (
            <CollectionDescriptionMarkdown value={value} />
          ) : (
            <p className="text-muted-foreground">
              {t('documentCollections:descriptionPreviewEmpty')}
            </p>
          )}
        </div>
      ) : (
        <Textarea
          id={id}
          autoFocus={autoFocus}
          aria-label={label}
          value={value}
          disabled={disabled}
          maxLength={collectionDescriptionMaxLength}
          placeholder={placeholder}
          onBlur={onBlur}
          onChange={(event) => onChange(event.target.value)}
        />
      )}
      <p className="text-xs text-muted-foreground">
        {t('documentCollections:descriptionMarkdownHint', {
          max: collectionDescriptionMaxLength,
        })}
      </p>
    </div>
  )
}

function PreviewToggle({
  onChange,
  previewing,
}: {
  onChange: (previewing: boolean) => void
  previewing: boolean
}) {
  const { t } = useTranslation('documentCollections')

  return (
    <div role="group" className="flex gap-1">
      <Button
        type="button"
        size="sm"
        variant={previewing ? 'ghost' : 'secondary'}
        aria-pressed={!previewing}
        onClick={() => onChange(false)}
      >
        {t('documentCollections:descriptionWrite')}
      </Button>
      <Button
        type="button"
        size="sm"
        variant={previewing ? 'secondary' : 'ghost'}
        aria-pressed={previewing}
        onClick={() => onChange(true)}
      >
        {t('documentCollections:descriptionPreview')}
      </Button>
    </div>
  )
}
